import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LockKey, Moon, SunDim } from "@phosphor-icons/react";

const BG_DARK = "https://static.prod-images.emergentagent.com/jobs/d3f87cc4-84d5-4cb7-9dec-f5c06c8f3d1d/images/fba2e9ed819a39549222073a8ca6a0635323ce9b886148c1335ad1f174f9be03.png";
const BG_LIGHT = "https://static.prod-images.emergentagent.com/jobs/d3f87cc4-84d5-4cb7-9dec-f5c06c8f3d1d/images/921c25c721a048e2e678cbbc07dc5392d44f2a93451555e3230359f3b8e069d7.png";

export default function Login() {
  const { user, loading, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) return <Navigate to="/" replace />;

  const handle = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  const bg = theme === "dark" ? BG_DARK : BG_LIGHT;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left: Brand / image */}
      <div
        className="hidden lg:flex relative items-end p-12 bg-cover bg-center"
        style={{ backgroundImage: `url(${bg})` }}
        data-testid="login-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="relative text-white">
          <div className="overline text-white/70 mb-4">Namecheap Operations Console</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tight leading-[0.95]">
            Bulk DNS.<br />
            Smart buying.<br />
            <span className="text-white/80">One clean panel.</span>
          </h1>
          <p className="mt-6 max-w-md text-sm text-white/70">
            Manage every domain in your Namecheap account, change nameservers in bulk, and
            discover new names with an AI naming assistant.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-6 md:p-12 relative">
        <button
          onClick={toggle}
          className="absolute top-6 right-6 h-9 w-9 rounded-md border border-border grid place-items-center hover:bg-muted transition-colors"
          data-testid="theme-toggle-login"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <SunDim size={16} /> : <Moon size={16} />}
        </button>

        <div className="w-full max-w-md animate-fade-in-up">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-md bg-primary grid place-items-center text-primary-foreground font-display font-black">
                NC
              </div>
              <div className="font-display font-bold text-lg tracking-tight">NameOps</div>
            </div>
            <div className="overline mb-2">Sign In</div>
            <h2 className="font-display text-3xl font-black tracking-tight">
              Welcome back.
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Admin credentials required to access the domain console.
            </p>
          </div>

          <form onSubmit={handle} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                data-testid="login-email"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="overline">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
                className="font-mono text-sm"
              />
            </div>
            {error && (
              <div className="text-xs text-destructive" data-testid="login-error">{error}</div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-10"
              data-testid="login-submit"
            >
              <LockKey size={16} className="mr-2" />
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-[11px] text-muted-foreground pt-2">
              Secured with JWT session cookies. API keys are encrypted at rest.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
